import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular/standalone';
import {
  AppUpdate,
  AppUpdateAvailability,
  AppUpdateInfo,
  OpenAppStoreOptions,
} from '@capawesome/capacitor-app-update';
import { StorageService } from '@movilidad/app-core';
import { compareVersions } from '@shared/utils';
import { UpdateReminderComponent } from '../component/update-reminder/update-reminder.component';

interface AppUpdateDetection {
  detectedAt: number;
  availableVersion: string;
}

const STORAGE_KEY = 'app_update_detected';
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private modalCtrl = inject(ModalController);
  private storage = inject(StorageService);
  private isShowingModal = false;

  protected readonly appStoreOptions: OpenAppStoreOptions = {
    appId: '1577332718',
    androidPackageName: 'app.technit.movilidadtec',
  };

  async checkForUpdate(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      const info = await AppUpdate.getAppUpdateInfo();

      if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
        await this.storage.remove(STORAGE_KEY);
        return;
      }

      if (Capacitor.getPlatform() === 'android' && info.immediateUpdateAllowed) {
        await this.handleImmediateUpdate();
        return;
      }

      await this.handleSoftUpdate(info);
    } catch (error) {
      console.error('AppUpdateService: error checking for update', error);
    }
  }

  private async handleSoftUpdate(info: AppUpdateInfo): Promise<void> {
    const platform = Capacitor.getPlatform();
    const availableVersion = platform === 'ios' ? info.availableVersionName : info.availableVersionCode;
    const currentVersion = platform === 'ios' ? info.currentVersionName : info.currentVersionCode;

    if (!availableVersion) {
      return;
    }

    const stored = await this.storage.get<AppUpdateDetection>(STORAGE_KEY);

    if (stored) {
      if (this.hasBeenUpdated(stored.availableVersion, currentVersion, platform)) {
        await this.storeDetection(availableVersion);
        return;
      }

      if (stored.availableVersion !== availableVersion) {
        await this.storage.set<AppUpdateDetection>(STORAGE_KEY, {
          detectedAt: stored.detectedAt,
          availableVersion,
        });
      }

      const elapsed = Date.now() - stored.detectedAt;
      if (elapsed >= TWENTY_FOUR_HOURS_MS) {
        await this.showUpdateModal();
      }
    } else {
      await this.storeDetection(availableVersion);
    }
  }

  private hasBeenUpdated(storedVersion: string, currentVersion: string, platform: string): boolean {
    if (platform === 'ios') {
      return compareVersions(storedVersion, currentVersion) <= 0;
    }
    return Number(storedVersion) <= Number(currentVersion);
  }

  private async storeDetection(availableVersion: string): Promise<void> {
    await this.storage.set<AppUpdateDetection>(STORAGE_KEY, {
      detectedAt: Date.now(),
      availableVersion,
    });
  }

  private async handleImmediateUpdate(): Promise<void> {
    try {
      await AppUpdate.performImmediateUpdate();
    } catch (error) {
      console.error('AppUpdateService: error performing immediate update', error);
    }
  }

  private async showUpdateModal(): Promise<void> {
    if (this.isShowingModal) return;
    this.isShowingModal = true;

    const modal = await this.modalCtrl.create({
      component: UpdateReminderComponent,
      cssClass: 'update-reminder-modal',
      breakpoints: [0, 0.6, 0.75],
      initialBreakpoint: 0.6,
    });

    await modal.present();

    const { role } = await modal.onWillDismiss();
    this.isShowingModal = false;

    if (role === 'update') {
      await AppUpdate.openAppStore(this.appStoreOptions);
    }
  }
}
